/* Lojas online (marketplaces) — calculadora de preço recomendado pra anunciar
   em Mercado Livre, Shopee, Amazon etc.

   ── De onde vem a tabela de taxas ──────────────────────────────────────
   A tabela de verdade NÃO é a que está neste arquivo: ela é buscada na
   fonte oficial de cada loja uma vez por semana pela function
   `atualizarTaxasLojas` e fica em config/taxasLojas no Firestore (ver
   functions/taxasLojas/). O hook useTaxasLojas() lê de lá.

   LOJAS_PADRAO abaixo é só o plano B pra quando o doc ainda não existe
   (conta nova, primeiro run do job, app offline). Se você encontrar uma
   divergência, a fonte oficial é que manda — não edite estes números
   achando que resolve, porque o job sobrescreve.

   ── Formato ────────────────────────────────────────────────────────────
   Cada loja tem "faixas" por preço de anúncio (`ate`), porque tanto a
   comissão quanto a taxa fixa mudam conforme a faixa de preço em algumas
   delas. `tipoFixo` é "fixo" (R$) ou "pct" (% do preço de anúncio). A
   última faixa de cada loja tem `ate: null`, valendo pra qualquer preço
   acima da faixa anterior. */
export const LOJAS_PADRAO = [
  {
    id: "ml", nome: "Mercado Livre",
    faixas: [
      { ate: 12.5,  comissao: 12, tipoFixo: "pct",  valorFixo: 50 },   // até R$12,50: taxa fixa = 50% do valor
      { ate: 29,    comissao: 12, tipoFixo: "fixo", valorFixo: 6.25 },
      { ate: 50,    comissao: 12, tipoFixo: "fixo", valorFixo: 6.50 },
      { ate: 79,    comissao: 12, tipoFixo: "fixo", valorFixo: 6.75 },
      { ate: null,  comissao: 12, tipoFixo: "fixo", valorFixo: 0 },     // acima de R$79: só a comissão
    ],
  },
  {
    id: "shopee", nome: "Shopee",
    faixas: [
      { ate: 8,      comissao: 20, tipoFixo: "pct",  valorFixo: 50 },  // exceção: item < R$8 -> taxa fixa = 50% do valor
      { ate: 79.99,  comissao: 20, tipoFixo: "fixo", valorFixo: 4 },
      { ate: 99.99,  comissao: 14, tipoFixo: "fixo", valorFixo: 16 },
      { ate: 199.99, comissao: 14, tipoFixo: "fixo", valorFixo: 20 },
      { ate: null,   comissao: 14, tipoFixo: "fixo", valorFixo: 26 },
    ],
  },
  {
    id: "amazon", nome: "Amazon",
    faixas: [{ ate: null, comissao: 15, tipoFixo: "fixo", valorFixo: 0 }],
  },
];

/* Resolve qual tabela usar: a viva (Firestore, via useTaxasLojas) quando
   existir, senão a embutida. Aceita também o formato antigo
   (cfg.lojasOnline), de quando dava pra editar a tabela na mão. */
export function listaLojas(tabela) {
  if (Array.isArray(tabela) && tabela.length) return tabela;
  if (tabela?.lojas?.length) return tabela.lojas;
  if (tabela?.lojasOnline?.length) return tabela.lojasOnline;
  return LOJAS_PADRAO;
}

/* Um reajuste já anunciado pela loja fica guardado em `agendado` até a data
   de vigência chegar — aí passa a valer sozinho, sem esperar o próximo run
   do job semanal. */
export function faixasVigentes(loja, hoje = new Date()) {
  const agendado = loja?.agendado;
  if (!agendado?.vigenciaEm || !Array.isArray(agendado.faixas)) return loja?.faixas || [];
  const quando = Date.parse(`${agendado.vigenciaEm}T00:00:00-03:00`);
  return Number.isFinite(quando) && quando <= hoje.getTime() ? agendado.faixas : (loja?.faixas || []);
}

/* Compat: loja salva no formato antigo (comissão única + taxaFixa único,
   sem faixas por preço) vira uma única faixa sem limite. */
function faixasDaLoja(loja, hoje) {
  const faixas = faixasVigentes(loja, hoje);
  if (Array.isArray(faixas) && faixas.length) return faixas;
  return [{ ate: null, comissao: loja?.comissao || 0, tipoFixo: "fixo", valorFixo: loja?.taxaFixa || 0 }];
}

const centavos = (v) => Math.round(v * 100);
const reais = (c) => c / 100;

/** Quanto a loja fica com um anúncio de `preco`, na faixa informada. */
function taxaDaFaixa(preco, faixa) {
  const comissao = preco * (Math.max(0, Number(faixa.comissao) || 0) / 100);
  const valorFixo = Math.max(0, Number(faixa.valorFixo) || 0);
  const extra = faixa.tipoFixo === "pct" ? preco * (valorFixo / 100) : valorFixo;
  return comissao + extra;
}

/* Caminho inverso de calcularPrecoLoja: o preço já está dado (venda feita)
   e a pergunta é quanto a loja fica com ele. A faixa é a do próprio preço —
   a primeira, em ordem crescente, cujo teto comporta o preço.
   Retorna { taxa, faixa }. */
export function taxaDaLoja(preco, loja, hoje = new Date()) {
  const valor = Math.max(0, Number(preco) || 0);
  const faixas = faixasDaLoja(loja, hoje)
    .map(f => ({ ...f, ate: (f.ate === "" || f.ate == null) ? Infinity : Number(f.ate) }))
    .sort((a, b) => a.ate - b.ate);
  const faixa = faixas.find(f => centavos(valor) <= centavos(f.ate)) || faixas[faixas.length - 1] || null;
  return { taxa: faixa && valor > 0 ? taxaDaFaixa(valor, faixa) : 0, faixa };
}

/* valorDesejado = quanto o vendedor quer receber líquido pela venda.
   Calcula o preço de anúncio pra que, depois de descontada a comissão (%) e
   a taxa fixa da faixa correspondente, sobre pelo menos valorDesejado.

   A faixa depende do próprio preço que estamos calculando, então testa em
   ordem crescente e só aceita a faixa quando o preço cai DENTRO dela — teto
   e piso. Checar só o teto (como era antes) fazia o cálculo devolver preço
   de uma faixa mais barata do que a que ele realmente cairia, e o vendedor
   recebia menos do que pediu.

   Quando nenhum preço "fecha" numa faixa (as taxas dão um salto no limite,
   então existe um vão), o menor preço que garante o líquido é um centavo
   acima do teto da faixa anterior — nesse caso o vendedor recebe um pouco
   mais do que pediu, nunca menos. Por isso `liquido` vem junto no retorno.

   Retorna { precoAnunciar, taxaTotal, liquido, faixa }. */
export function calcularPrecoLoja(valorDesejado, loja, hoje = new Date()) {
  const valor = Math.max(0, Number(valorDesejado) || 0);
  const faixas = faixasDaLoja(loja, hoje)
    .map(f => ({ ...f, ate: (f.ate === "" || f.ate == null) ? Infinity : Number(f.ate) }))
    .sort((a, b) => a.ate - b.ate);

  const resultado = (preco, faixa) => {
    const taxaTotal = taxaDaFaixa(preco, faixa);
    return { precoAnunciar: preco, taxaTotal, liquido: preco - taxaTotal, faixa };
  };

  if (!faixas.length) return { precoAnunciar: valor, taxaTotal: 0, liquido: valor, faixa: null };
  if (valor <= 0) return resultado(0, faixas[0]);

  let pisoCents = 0; // teto da faixa anterior, em centavos
  let ultimo = null;
  for (const faixa of faixas) {
    const comissaoFrac = Math.max(0, Number(faixa.comissao) || 0) / 100;
    const extraPct = faixa.tipoFixo === "pct" ? Math.max(0, Number(faixa.valorFixo) || 0) / 100 : 0;
    const extraFixo = faixa.tipoFixo === "pct" ? 0 : Math.max(0, Number(faixa.valorFixo) || 0);
    const divisor = 1 - comissaoFrac - extraPct;

    const bruto = divisor > 0 ? (valor + extraFixo) / divisor : valor + extraFixo;
    // Arredonda pra cima no centavo: nunca deixar o líquido ficar um
    // centavo abaixo do que o vendedor pediu por causa de arredondamento.
    const precoCents = Math.max(Math.ceil(bruto * 100), pisoCents + 1);
    const preco = reais(precoCents);

    ultimo = resultado(preco, faixa);
    if (precoCents <= centavos(faixa.ate)) return ultimo;
    pisoCents = centavos(faixa.ate);
  }
  return ultimo;
}
